import { toArray } from '@enmon/commons';
import { isEmpty, isNil } from 'ramda';
import { Children, type FC, isValidElement, type Key, type PropsWithChildren, type ReactNode } from 'react';

export interface ContentSwitchChildProps {
  id: Key;
}

export interface ContentSwitchProps {
  /**
   * List of files to display in gallery.
   */
  activeId?: Key | Key[];
  /**
   * Content shown as an placeholder for a case of no activeId provided.
   */
  initialContent?: ReactNode;
  /**
   * Content shown as an placeholder for a case of activeId not matching any children ID.
   */
  notFoundContent?: ReactNode;
}

export const ContentSwitch: FC<PropsWithChildren<ContentSwitchProps>> = ({
  activeId,
  children,
  initialContent,
  notFoundContent,
}) => {
  const listOfIds = toArray(activeId);

  // parse ids to string due to type consistency
  const activeStringIds = listOfIds
    .filter((currentId): currentId is Key => !isNil(currentId))
    .map(currentId => currentId.toString());

  const arrayChildren = Children.toArray(children);

  // filter children by active ids - shows only children with ID matches activeId/s
  const filteredChildren = arrayChildren.filter(
    child =>
      isValidElement<ContentSwitchChildProps>(child) && activeStringIds.includes(child?.props?.id?.toString() ?? ''),
  );

  const content = filteredChildren.length > 0 ? filteredChildren : notFoundContent;

  // if no ids were provided return initialContent
  return <>{!isEmpty(activeStringIds) ? content : initialContent}</>;
};

export const Case: FC<PropsWithChildren<ContentSwitchChildProps>> = ({ children }) => <>{children}</>;
